import * as React from 'react'
import { CalendarIcon, Clock } from 'lucide-react'
import {
  addDays,
  endOfMonth,
  endOfWeek,
  format,
  isValid,
  parse,
  startOfMonth,
  startOfWeek,
} from 'date-fns'
import { it } from 'date-fns/locale'
import { dateMatchModifiers } from 'react-day-picker'

import { Calendar } from './calendar'
import { Input } from './input'
import { Popover, PopoverContent, PopoverTrigger } from './popover'
import type { Matcher } from 'react-day-picker'
import { cn } from '@/core/utils'

interface DatePickerProps {
  date?: Date
  onDateChange?: (date: Date | undefined) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  formatStr?: string
  label?: string
  error?: string
  required?: boolean
  clearable?: boolean
  showShortcuts?: boolean
  minDate?: Date
  maxDate?: Date
  calendarProps?: Partial<React.ComponentProps<typeof Calendar>>
}

interface DateShortcut {
  label: string
  date: Date
  description: string
}

// Memoized shortcut button component
const ShortcutButton = React.memo(function ShortcutButton({
  shortcut,
  onClick,
}: {
  shortcut: DateShortcut
  onClick: () => void
}) {
  const handleClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onClick()
    },
    [onClick],
  )

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        'w-full text-left px-3 py-2.5 rounded-md transition-all duration-150',
        'hover:bg-primary/10 active:bg-primary/15',
        'focus:outline-none focus:ring-2 focus:ring-primary/20 focus:bg-primary/5',
        'group',
      )}
    >
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
          {shortcut.label}
        </span>
        <span className="text-xs text-muted-foreground">
          {shortcut.description}
        </span>
      </div>
    </button>
  )
})

// Memoized shortcuts panel
const ShortcutsPanel = React.memo(function ShortcutsPanel({
  shortcuts,
  onSelect,
}: {
  shortcuts: Array<DateShortcut>
  onSelect: (date: Date) => void
}) {
  return (
    <div className="hidden sm:block border-r border-border bg-muted/20 p-2 min-w-[180px] max-w-[200px]">
      <div className="flex items-center gap-2 px-3 py-2 mb-1">
        <Clock className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          Scorciatoie
        </span>
      </div>
      <div className="space-y-0.5">
        {shortcuts.map((shortcut, idx) => (
          <ShortcutButton
            key={idx}
            shortcut={shortcut}
            onClick={() => onSelect(shortcut.date)}
          />
        ))}
      </div>
    </div>
  )
})

/*
// Memoized clear button
const ClearDateButton = React.memo(function ClearDateButton({
  onClick,
}: {
  onClick: (e: React.MouseEvent) => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "absolute right-9 top-1/2 -translate-y-1/2 z-10",
        "p-1 rounded-md",
        "text-muted-foreground hover:text-destructive",
        "hover:bg-destructive/10",
        "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1",
        "transition-colors"
      )}
      aria-label="Clear date"
    >
      <X className="h-4 w-4" />
    </button>
  )
})*/

// Generate shortcuts - stable function
const createShortcuts = (): Array<DateShortcut> => {
  const now = new Date()
  return [
    {
      label: 'Oggi',
      date: now,
      description: format(now, 'EEEE d MMMM', { locale: it }),
    },
    {
      label: 'Domani',
      date: addDays(now, 1),
      description: format(addDays(now, 1), 'EEEE d MMMM', { locale: it }),
    },
    {
      label: 'Tra 7 giorni',
      date: addDays(now, 7),
      description: format(addDays(now, 7), 'EEEE d MMMM', { locale: it }),
    },
    {
      label: 'Inizio settimana',
      date: startOfWeek(now, { weekStartsOn: 1 }),
      description: format(startOfWeek(now, { weekStartsOn: 1 }), 'd MMMM', {
        locale: it,
      }),
    },
    {
      label: 'Fine settimana',
      date: endOfWeek(now, { weekStartsOn: 1 }),
      description: format(endOfWeek(now, { weekStartsOn: 1 }), 'd MMMM', {
        locale: it,
      }),
    },
    {
      label: 'Inizio mese',
      date: startOfMonth(now),
      description: format(startOfMonth(now), 'd MMMM yyyy', { locale: it }),
    },
    {
      label: 'Fine mese',
      date: endOfMonth(now),
      description: format(endOfMonth(now), 'd MMMM yyyy', { locale: it }),
    },
  ]
}

const DatePicker = React.memo(function DatePicker({
  date,
  onDateChange,
  placeholder = 'Pick a date',
  disabled = false,
  label,
  error,
  required = false,
  showShortcuts = true,
  minDate,
  maxDate,
  calendarProps,
  formatStr = 'dd/MM/yyyy',
}: DatePickerProps) {
  const [isOpen, setIsOpen] = React.useState(false)

  const [currentMonth, setCurrentMonth] = React.useState<Date>(() => {
    if (date && isValid(date)) return date
    if (calendarProps?.defaultMonth && isValid(calendarProps.defaultMonth))
      return calendarProps.defaultMonth
    if (calendarProps?.month && isValid(calendarProps.month))
      return calendarProps.month
    return new Date()
  })

  // Memoize shortcuts - only recalculate when popover opens
  const shortcuts = React.useMemo(() => createShortcuts(), [isOpen])

  const handleShortcutClick = React.useCallback(
    (shortcutDate: Date) => {
      onDateChange?.(shortcutDate)
      setCurrentMonth(shortcutDate)
      setInputValue(format(shortcutDate, formatStr))
      setIsOpen(false)
    },
    [formatStr, onDateChange],
  )
  /*
    const handleClear = React.useCallback((e: React.MouseEvent) => {
      e.stopPropagation()
      onDateChange?.(undefined)
    }, [onDateChange])*/

  const handleSelect = React.useCallback(
    (selectedDate: Date | undefined) => {
      onDateChange?.(selectedDate)
      if (selectedDate && isValid(selectedDate)) {
        setCurrentMonth(selectedDate)
        setInputValue(format(selectedDate, formatStr))
      }
      setIsOpen(false)
    },
    [formatStr, onDateChange],
  )

  const [inputValue, setInputValue] = React.useState(
    date && isValid(date) ? format(date, formatStr) : '',
  )

  React.useEffect(() => {
    if (date && isValid(date)) {
      setInputValue(format(date, formatStr))
      setCurrentMonth(date)
    } else if (!date) {
      setInputValue('')
    }
  }, [date, formatStr])

  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      if (open && date && isValid(date)) {
        setCurrentMonth(date)
      }
      setIsOpen(open)
    },
    [date],
  )

  const handleMonthChange = React.useCallback(
    (newMonth: Date) => {
      setCurrentMonth(newMonth)
      calendarProps?.onMonthChange?.(newMonth)
    },
    [calendarProps?.onMonthChange],
  )

  // Construct combined disabled matchers
  const disabledMatchers = React.useMemo(() => {
    const matchers: Array<Matcher> = []

    // Min/Max matcher
    matchers.push((candidateDate: Date) => {
      if (minDate && candidateDate < minDate) return true
      if (maxDate && candidateDate > maxDate) return true
      return false
    })

    // External matcher(s)
    if (calendarProps?.disabled) {
      if (Array.isArray(calendarProps.disabled)) {
        matchers.push(...calendarProps.disabled)
      } else {
        matchers.push(calendarProps.disabled)
      }
    }

    return matchers
  }, [minDate, maxDate, calendarProps?.disabled])

  // Helper function to check if a date is disabled
  const isDateDisabled = React.useCallback(
    (dateToCheck: Date) => {
      if (!isValid(dateToCheck)) return true
      return dateMatchModifiers(dateToCheck, disabledMatchers)
    },
    [disabledMatchers],
  )

  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (inputValue.length === 10) {
        const parsedDate = parse(inputValue, formatStr, new Date())

        if (isValid(parsedDate) && !isDateDisabled(parsedDate)) {
          if (!date || inputValue !== format(date, formatStr)) {
            handleSelect(parsedDate)
          }
        }
      }
    }, 1500)

    return () => clearTimeout(timer)
  }, [date, formatStr, handleSelect, inputValue, isDateDisabled])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value)
  }

  // Check if input value represents an invalid or disabled date
  const isInputInvalid = React.useMemo(() => {
    if (inputValue.length !== 10) return false
    const parsedDate = parse(inputValue, formatStr, new Date())
    return isDateDisabled(parsedDate)
  }, [formatStr, inputValue, isDateDisabled])

  // Memoize label className
  const labelClassName = React.useMemo(
    () =>
      cn(
        'text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
        error && 'text-destructive',
      ),
    [error],
  )
  return (
    <div className="flex flex-col gap-2 w-full">
      {label && (
        <label className={labelClassName}>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
      )}
      <div className="relative group">
        <Input
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          className={cn(
            'pr-10',
            (error || isInputInvalid) &&
              'border-destructive focus-visible:ring-destructive/20',
            date && !isInputInvalid && 'border-primary/30 bg-primary/5',
          )}
          disabled={disabled}
          aria-invalid={!!error || isInputInvalid}
        />
        <Popover open={isOpen} onOpenChange={handleOpenChange}>
          <PopoverTrigger
            render={(props) => (
              <button
                {...props}
                type="button"
                className={cn(
                  'absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md',
                  'text-muted-foreground hover:text-primary',
                  'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1',
                  'transition-colors',
                )}
                disabled={disabled}
              >
                <CalendarIcon
                  className={cn(
                    'h-4 w-4 shrink-0 transition-colors',
                    date ? 'text-primary' : 'text-muted-foreground',
                  )}
                />
              </button>
            )}
          />
          <PopoverContent className="w-auto p-0 shadow-lg">
            <div className="flex rounded-md overflow-hidden bg-popover">
              {showShortcuts && (
                <ShortcutsPanel
                  shortcuts={shortcuts}
                  onSelect={handleShortcutClick}
                />
              )}
              <div className="p-1">
                <Calendar
                  {...calendarProps}
                  mode="single"
                  selected={date}
                  month={calendarProps?.month ?? currentMonth}
                  onMonthChange={handleMonthChange}
                  onSelect={(selectedDate) => {
                    if (selectedDate) {
                      handleSelect(selectedDate)
                    }
                  }}
                  disabled={disabledMatchers}
                  locale={it}
                />
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {/* clearable && date && !disabled && (
          <ClearDateButton onClick={handleClear} />
        )*/}
      </div>
      {isInputInvalid && date && !isDateDisabled(date) && (
        <p className="text-xs text-muted-foreground">
          Data non valida, ultima data valida inserita:{' '}
          {format(date, formatStr)}
        </p>
      )}
      {error && (
        <p
          className="text-sm text-destructive flex items-center gap-1.5"
          role="alert"
        >
          <span className="inline-block w-1 h-1 rounded-full bg-destructive" />
          {error}
        </p>
      )}
    </div>
  )
})

DatePicker.displayName = 'DatePicker'

export { DatePicker }
export type { DatePickerProps }
